import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Hover tooltip that renders into a portal so it can't be clipped by card
// overflow, and clamps itself to the viewport (shifts horizontally, flips
// above when there's no room below). Pure JS positioning — no dependency.
export default function HoverTooltip({ children, content, triggerClassName, widthClassName = 'w-72' }) {
  const triggerRef = useRef(null);
  const tipRef = useRef(null);
  const [rect, setRect] = useState(null);
  const [style, setStyle] = useState(null);

  useLayoutEffect(() => {
    if (!rect || !tipRef.current) return;
    const tip = tipRef.current.getBoundingClientRect();
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = rect.left + rect.width / 2 - tip.width / 2;
    left = Math.max(margin, Math.min(left, vw - tip.width - margin));
    let top = rect.bottom + 6;
    if (top + tip.height > vh - margin) {
      top = Math.max(margin, rect.top - tip.height - 6);
    }
    setStyle({ position: 'fixed', top, left, zIndex: 9999 });
  }, [rect]);

  const open = () => setRect(triggerRef.current?.getBoundingClientRect() || null);
  const close = () => { setRect(null); setStyle(null); };

  return (
    <span
      ref={triggerRef}
      className={triggerClassName}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
    >
      {children}
      {rect && createPortal(
        <div
          ref={tipRef}
          role="tooltip"
          style={style || { position: 'fixed', top: rect.bottom + 6, left: rect.left, visibility: 'hidden', zIndex: 9999 }}
          className={`${widthClassName} max-w-[calc(100vw-1rem)] pointer-events-none`}
        >
          <span className="block rounded-lg border border-white/20 bg-gray-900 px-4 py-3 text-sm leading-relaxed text-gray-100 shadow-2xl text-left font-normal normal-case">
            {content}
          </span>
        </div>,
        document.body,
      )}
    </span>
  );
}
